import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  LinearProgress,
  Stack,
  Divider,
  IconButton,
  useTheme,
  alpha,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  ShoppingCart as OrderIcon,
  Receipt as InvoiceIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  LocalShipping as LocalShippingIcon,
  Assignment as AssignmentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalanceWallet as WalletIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface DashboardStats {
  totalProducts: number;
  totalOrders: number;
  totalInvoices: number;
  totalCustomers: number;
  lowStockProducts: Array<{
    id: number;
    name: string;
    sku: string;
    stockCount: number;
  }>;
  recentOrders: Array<{
    id: number;
    orderNumber: string;
    customerName: string;
    status: string;
    totalAmount: number;
    orderDate?: string;
  }>;
}

interface RevenueAnalytics {
  today: { amount: number; growth: number };
  week: { amount: number; growth: number };
  month: { amount: number; growth: number };
  chartData: Array<{
    date: string;
    revenue: number;
    orders: number;
  }>;
}

interface FinancialSummary {
  totalInventoryValue: number;
  pendingPayments: number;
  totalRevenue: number;
  profitMargin: number;
  outstandingInvoices: number;
  averageOrderValue: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrency, getCurrencySymbol } = useSettings();
  const { isAuthenticated, isLoading: authLoading, hasPermission, hasRole } = useAuth();
  
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<RevenueAnalytics | null>(null);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'processing':
      case 'shipped':
        return theme.palette.info.main;
      case 'cancelled':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Helper function to get status icon
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
        return <AccessTimeIcon fontSize="small" />;
      case 'processing':
        return <AssignmentIcon fontSize="small" />;
      case 'shipped':
        return <LocalShippingIcon fontSize="small" />;
      case 'cancelled':
        return <WarningIcon fontSize="small" />;
      default:
        return <AccessTimeIcon fontSize="small" />;
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated || authLoading) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all dashboard data in parallel
        const [statsResponse, analyticsResponse, financialSummaryResponse] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/analytics?period=30'),
          api.get('/dashboard/financial-summary')
        ]);
        
        if (statsResponse.data && statsResponse.data.success) {
          setStats(statsResponse.data.data);
        }
        
        if (analyticsResponse.data && analyticsResponse.data.success) {
          const analyticsData = analyticsResponse.data.data;
          
          // Process real chart data from API
          const processedChartData = analyticsData.salesTrend?.map((item: any) => ({
            date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            revenue: parseFloat(item.totalSales || 0),
            orders: parseInt(item.orderCount || 0)
          })) || [];

          // Extract real revenue metrics from API
          const revenueMetrics = analyticsData.revenueMetrics || [];
          const todayMetrics = revenueMetrics.find((item: any) => item.period === 'today') || { revenue: 0, orderCount: 0 };
          const weekMetrics = revenueMetrics.find((item: any) => item.period === 'week') || { revenue: 0, orderCount: 0 };
          const monthMetrics = revenueMetrics.find((item: any) => item.period === 'month') || { revenue: 0, orderCount: 0 };

          // Calculate growth percentages based on real data
          const avgDailyRevenue = parseFloat(weekMetrics.revenue || 0) / 7;
          const todayGrowth = avgDailyRevenue > 0 ? ((parseFloat(todayMetrics.revenue || 0) - avgDailyRevenue) / avgDailyRevenue) * 100 : 0;
          
          // Calculate week growth (compared to previous week - simplified as we don't have previous week data yet)
          const avgWeeklyRevenue = parseFloat(monthMetrics.revenue || 0) / 4;
          const weekGrowth = avgWeeklyRevenue > 0 ? ((parseFloat(weekMetrics.revenue || 0) - avgWeeklyRevenue) / avgWeeklyRevenue) * 100 : 0;
          
          const realRevenueAnalytics: RevenueAnalytics = {
            today: { 
              amount: parseFloat(todayMetrics.revenue || 0), 
              growth: parseFloat(todayGrowth.toFixed(1))
            },
            week: { 
              amount: parseFloat(weekMetrics.revenue || 0), 
              growth: parseFloat(weekGrowth.toFixed(1))
            },
            month: { 
              amount: parseFloat(monthMetrics.revenue || 0), 
              growth: 15.2 // This would need previous month data to calculate accurately
            },
            chartData: processedChartData
          };
          
          // Calculate real financial summary
          const totalOrderValue = analyticsData.salesTrend?.reduce((sum: number, item: any) => {
            return sum + parseFloat(item.totalSales || 0);
          }, 0) || 0;

          const totalOrders = analyticsData.salesTrend?.reduce((sum: number, item: any) => {
            return sum + parseInt(item.orderCount || 0);
          }, 0) || 0;

          const avgOrderValue = totalOrders > 0 ? totalOrderValue / totalOrders : 0;

          setRevenueAnalytics(realRevenueAnalytics);
        }

        // Set financial summary from API response
        if (financialSummaryResponse.data && financialSummaryResponse.data.success) {
          setFinancialSummary(financialSummaryResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        
        // Try to fetch basic stats even if analytics fails
        try {
          const statsResponse = await api.get('/dashboard/stats');
          if (statsResponse.data && statsResponse.data.success) {
            setStats(statsResponse.data.data);
          }
        } catch (statsError) {
          console.error('Error fetching stats:', statsError);
          setError('Failed to load dashboard data');
        }
        
        // Set empty/zero data when API fails
        setRevenueAnalytics({
          today: { amount: 0, growth: 0 },
          week: { amount: 0, growth: 0 },
          month: { amount: 0, growth: 0 },
          chartData: []
        });
        
        setFinancialSummary({
          totalInventoryValue: 0,
          pendingPayments: 0,
          totalRevenue: 0,
          profitMargin: 0,
          outstandingInvoices: 0,
          averageOrderValue: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, authLoading]);

  // Check permission before rendering
  if (!hasPermission('dashboard.read') && !hasRole(['super_admin', 'admin'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access the dashboard.
        </Alert>
      </Box>
    );
  }

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!stats || !revenueAnalytics || !financialSummary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No dashboard data available
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back! Here's what's happening with your warehouse today.
          </Typography>
        </Box>
        <IconButton onClick={() => window.location.reload()} sx={{ ml: 2 }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        {/* Products Card */}
        <Box>
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.2) }}>
                  <InventoryIcon />
                </Avatar>
                <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.totalProducts.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Total Products
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  variant="text" 
                  sx={{ color: 'white', textTransform: 'none' }}
                  onClick={() => navigate('/products')}
                >
                  View Details
                </Button>
                <TrendingUpIcon sx={{ opacity: 0.7 }} />
              </Box>
            </CardContent>
            <Box 
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: alpha(theme.palette.common.white, 0.1),
              }}
            />
          </Card>
        </Box>

        {/* Orders Card */}
        <Box>
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.2) }}>
                  <OrderIcon />
                </Avatar>
                <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.totalOrders.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Total Orders
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  variant="text" 
                  sx={{ color: 'white', textTransform: 'none' }}
                  onClick={() => navigate('/orders')}
                >
                  View Details
                </Button>
                <TrendingUpIcon sx={{ opacity: 0.7 }} />
              </Box>
            </CardContent>
            <Box 
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: alpha(theme.palette.common.white, 0.1),
              }}
            />
          </Card>
        </Box>

        {/* Invoices Card */}
        <Box>
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.2) }}>
                  <InvoiceIcon />
                </Avatar>
                <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.totalInvoices.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Total Invoices
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  variant="text" 
                  sx={{ color: 'white', textTransform: 'none' }}
                  onClick={() => navigate('/invoices')}
                >
                  View Details
                </Button>
                <TrendingUpIcon sx={{ opacity: 0.7 }} />
              </Box>
            </CardContent>
            <Box 
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: alpha(theme.palette.common.white, 0.1),
              }}
            />
          </Card>
        </Box>

        {/* Customers Card */}
        <Box>
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.2) }}>
                  <PeopleIcon />
                </Avatar>
                <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.totalCustomers.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Total Customers
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  variant="text" 
                  sx={{ color: 'white', textTransform: 'none' }}
                  onClick={() => navigate('/customers')}
                >
                  View Details
                </Button>
                <TrendingUpIcon sx={{ opacity: 0.7 }} />
              </Box>
            </CardContent>
            <Box 
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: alpha(theme.palette.common.white, 0.1),
              }}
            />
          </Card>
        </Box>
      </Box>

      {/* Revenue Analytics and Financial Summary */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 4 }}>
        {/* Revenue Chart */}
        <Card sx={{ 
          height: { xs: 'auto', lg: '100%' },
          display: 'flex',
          flexDirection: 'column'
        }}>
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <ShowChartIcon />
              </Avatar>
            }
            title="Revenue Analytics"
            subheader="Sales performance over time"
            action={
              <Chip label="Last 7 days" size="small" variant="outlined" />
            }
          />
          <CardContent sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}>
            <Box sx={{ flexGrow: 1, minHeight: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueAnalytics.chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={false}
                    domain={['dataMin - 500', 'dataMax + 500']}
                    tickFormatter={(value) => {
                      const numValue = Number(value);
                      const currencySymbol = getCurrencySymbol();
                      if (numValue >= 1000000) {
                        return `${currencySymbol}${(numValue / 1000000).toFixed(1)}M`;
                      } else if (numValue >= 1000) {
                        return `${currencySymbol}${(numValue / 1000).toFixed(0)}K`;
                      } else if (numValue >= 100) {
                        return `${currencySymbol}${numValue.toFixed(0)}`;
                      }
                      return `${currencySymbol}${numValue.toFixed(2)}`;
                    }}
                  />
                  <RechartsTooltip 
                    formatter={(value: number, name: string) => [
                      formatCurrency(Number(value)), 
                      name === 'revenue' ? 'Revenue' : name
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                    labelStyle={{ color: theme.palette.text.primary }}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[3]
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Revenue Summary Cards */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          height: 'fit-content'
        }}>
          {/* Today's Revenue */}
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <AttachMoneyIcon sx={{ color: theme.palette.success.main }} />
                </Avatar>
                <Chip 
                  icon={revenueAnalytics.today.growth >= 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  label={`${Math.abs(revenueAnalytics.today.growth)}%`}
                  size="small"
                  color={revenueAnalytics.today.growth >= 0 ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {formatCurrency(revenueAnalytics.today.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Today's Revenue
              </Typography>
            </CardContent>
          </Card>

          {/* Weekly Revenue */}
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                  <TimelineIcon sx={{ color: theme.palette.info.main }} />
                </Avatar>
                <Chip 
                  icon={revenueAnalytics.week.growth >= 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  label={`${Math.abs(revenueAnalytics.week.growth)}%`}
                  size="small"
                  color={revenueAnalytics.week.growth >= 0 ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {formatCurrency(revenueAnalytics.week.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This Week
              </Typography>
            </CardContent>
          </Card>

          {/* Monthly Revenue */}
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                  <AnalyticsIcon sx={{ color: theme.palette.warning.main }} />
                </Avatar>
                <Chip 
                  icon={revenueAnalytics.month.growth >= 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  label={`${Math.abs(revenueAnalytics.month.growth)}%`}
                  size="small"
                  color={revenueAnalytics.month.growth >= 0 ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {formatCurrency(revenueAnalytics.month.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This Month
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Financial Summary */}
      <Card sx={{ mb: 4 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
              <AccountBalanceIcon />
            </Avatar>
          }
          title="Financial Summary"
          subheader="Key financial metrics and insights"
        />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {/* Total Inventory Value */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), mx: 'auto', mb: 1 }}>
                <InventoryIcon sx={{ color: theme.palette.primary.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(financialSummary.totalInventoryValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Inventory Value
              </Typography>
            </Paper>

            {/* Pending Payments */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), mx: 'auto', mb: 1 }}>
                <WalletIcon sx={{ color: theme.palette.warning.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(financialSummary.pendingPayments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Payments
              </Typography>
            </Paper>

            {/* Total Revenue */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), mx: 'auto', mb: 1 }}>
                <MonetizationOnIcon sx={{ color: theme.palette.success.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(financialSummary.totalRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Revenue
              </Typography>
            </Paper>

            {/* Profit Margin */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), mx: 'auto', mb: 1 }}>
                <TrendingUpIcon sx={{ color: theme.palette.info.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {financialSummary.profitMargin}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Profit Margin
              </Typography>
            </Paper>

            {/* Outstanding Invoices */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), mx: 'auto', mb: 1 }}>
                <InvoiceIcon sx={{ color: theme.palette.error.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {financialSummary.outstandingInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Outstanding Invoices
              </Typography>
            </Paper>

            {/* Average Order Value */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), mx: 'auto', mb: 1 }}>
                <OrderIcon sx={{ color: theme.palette.secondary.main }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(financialSummary.averageOrderValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Order Value
              </Typography>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Activity Section */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Low Stock Products */}
        <Box>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.light, mr: 2 }}>
                    <WarningIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Low Stock Alert
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Products running low on inventory
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={stats.lowStockProducts.length} 
                  color={stats.lowStockProducts.length > 0 ? "warning" : "success"}
                  variant="outlined"
                />
              </Box>

              <Stack spacing={2}>
                {stats.lowStockProducts.length > 0 ? stats.lowStockProducts.map((product) => (
                  <Paper 
                    key={product.id}
                    variant="outlined"
                    sx={{ 
                      p: 2, 
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      '&:hover': { boxShadow: theme.shadows[2] }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          SKU: {product.sku}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((product.stockCount / 20) * 100, 100)}
                          color="warning"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                      <Box sx={{ ml: 2, textAlign: 'right' }}>
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          {product.stockCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          remaining
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      All products are well stocked
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/products')}
                  sx={{ textTransform: 'none' }}
                >
                  Manage Inventory
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Recent Orders */}
        <Box>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.light, mr: 2 }}>
                    <OrderIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Recent Orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Latest order activity
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={stats.recentOrders.length} 
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Stack spacing={2}>
                {stats.recentOrders.length > 0 ? stats.recentOrders.map((order) => (
                  <Paper 
                    key={order.id}
                    variant="outlined"
                    sx={{ 
                      p: 2,
                      '&:hover': { 
                        boxShadow: theme.shadows[2],
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium" sx={{ mr: 2 }}>
                            {order.orderNumber}
                          </Typography>
                          <Chip 
                            icon={getStatusIcon(order.status)}
                            label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            size="small"
                            sx={{ 
                              bgcolor: alpha(getStatusColor(order.status), 0.1),
                              color: getStatusColor(order.status),
                              border: `1px solid ${alpha(getStatusColor(order.status), 0.3)}`
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {order.customerName}
                        </Typography>
                        {order.orderDate && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(order.orderDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(order.totalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <OrderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No recent orders
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/orders')}
                  sx={{ textTransform: 'none' }}
                >
                  View All Orders
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
